import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { vendorLabel } from '@/lib/providers/integration-state';
import {
  matchesFilter,
  orderRows,
  vendorRateLimit,
  type ProviderFilter,
  type RowFacts,
  type SectionRow,
  type ServiceSectionModel,
  type VendorAccount,
} from '@/lib/providers/providers-overview';
import { isLockedFirst, leaderWhy } from '@/lib/providers/provider-routing';
import type { ProviderStatus } from '@/lib/providers/types';
import { IntegrationRow, MemberRow, UnwiredChannelRow } from './integrations-card';
import { CardFooterLine, CardNotice, ProviderTable, ServiceCard, StrategyChip } from './provider-table';
import { RoutingBar, RoutingDrafts, RoutingMemberControls, RoutingStrategyControl } from './routing-controls';
import type { RoutingDraftApi } from './use-routing-draft';

/**
 * ONE SERVICE CARD — "what does this environment use for hotels?".
 *
 * Header: the service, "N in use · M total", what it is for, and how its
 * providers combine (the "Order of use" strategy when the category is routed —
 * editable there by a supervisor — or the category's selection policy
 * otherwise). Body: one compact row per provider in the path, routed ones first
 * in their order. Footer: the alternatives this build supports but does not use
 * here, collapsed into one muted line — except for CHANNELS, where a missing
 * reach is a named row (see the selection model in `integration-state.ts`).
 *
 * Filtering never unmounts a row — it hides it — so a probe result or an open
 * detail survives a trip through another filter.
 *
 * M2: one card per SERVICE. A row whose vendor serves other services too reads
 * as a binding of that one account (`accounts`). `lead` is a row that is not an
 * inventory binding — FlightAware via the predictor, on flight information —
 * counted and filtered like any other. It heads the card unless the order names
 * a provider locked FIRST (the airline always wins, 2026-10-29): that row comes
 * before it, with a lock on its "#".
 */
interface ServiceSectionProps {
  section: ServiceSectionModel;
  filter: ProviderFilter;
  traffic?: ProviderStatus[] | undefined;
  /** The vendors that serve several services, by vendor token. */
  accounts?: ReadonlyMap<string, VendorAccount> | undefined;
  lead?: { row: ReactNode; facts: RowFacts } | undefined;
}

export function ServiceSection(props: ServiceSectionProps) {
  return (
    <RoutingDrafts views={props.section.routing}>
      {(drafts) => <ServiceSectionBody {...props} drafts={drafts} />}
    </RoutingDrafts>
  );
}

function ServiceSectionBody({
  section,
  filter,
  traffic,
  accounts,
  lead,
  drafts,
}: ServiceSectionProps & { drafts: RoutingDraftApi[] }) {
  const { t } = useTranslation('network');
  const { category, group } = section;

  // The card reads in the order being drafted, so "#" and the rows never disagree.
  const primary = drafts[0];
  const order = primary ? primary.draft.members.map((m) => m.vendor) : [];
  const position = new Map(order.map((vendor, i) => [vendor, i + 1] as const));
  const rows = orderRows(section.rows, order);
  const facts = [...(lead ? [lead.facts] : []), ...rows.map((r) => r.facts)];
  const anyMatch = facts.some((f) => matchesFilter(f, filter));
  const inUse = facts.filter((f) => f.inUse).length;

  const routingFor = (vendor: string) => {
    const mine = drafts.filter((api) => api.draft.members.some((m) => m.vendor === vendor));
    if (mine.length === 0) return null;
    return (
      <div className="space-y-2">
        {mine.map((api) => (
          <RoutingMemberControls key={api.key} api={api} vendor={vendor} />
        ))}
      </div>
    );
  };

  // The airline always wins: a provider locked first leads the card, above the `lead` row.
  const lockedFirst = (vendor: string) => drafts.find((api) => isLockedFirst(api.view.leader, vendor));
  const lockedWhy = (vendor: string) => {
    const api = lockedFirst(vendor);
    return api?.view.leader ? `${t('providers.routing.leader.badge')}. ${leaderWhy(t, api.view, api.view.leader)}` : undefined;
  };
  const leading = rows.filter((r) => lockedFirst(r.vendor));
  const following = rows.filter((r) => !lockedFirst(r.vendor));

  const renderRow = (row: SectionRow) => {
    const hidden = !matchesFilter(row.facts, filter);
    const common = {
      position: position.get(row.vendor),
      locked: lockedWhy(row.vendor),
      routing: routingFor(row.vendor),
      hidden,
    };
    const account = accounts?.get(row.vendor);
    if (row.kind === 'integration') {
      return (
        <IntegrationRow
          key={row.id}
          descriptor={row.descriptor}
          rateLimit={vendorRateLimit(row.vendor, traffic)}
          account={account}
          {...common}
        />
      );
    }
    if (row.kind === 'unwired') {
      return <UnwiredChannelRow key={row.id} descriptor={row.descriptor} account={account} {...common} />;
    }
    return <MemberRow key={row.id} category={category} member={row.member} {...common} />;
  };

  const description = t(`providers.section.description.${category}`, { defaultValue: '' });

  return (
    <ServiceCard
      testId={`integration-category-${category}`}
      summaryTestId={`integration-category-summary-${category}`}
      selection={group.selection}
      hidden={filter !== 'all' && !anyMatch}
      title={t(`providers.integrations.category.${category}`, { defaultValue: category })}
      summary={t('providers.section.inUse', { inUse, total: facts.length })}
      description={description || undefined}
      chips={
        drafts.length > 0 ? (
          drafts.map((api) => <RoutingStrategyControl key={api.key} api={api} multi={drafts.length > 1} />)
        ) : (
          <StrategyChip
            title={t(`providers.integrations.selectionHint.${group.selection}`)}
            testId={`integration-strategy-${category}`}
          >
            {t(`providers.section.chip.${group.selection}`)}
          </StrategyChip>
        )
      }
      bars={
        <>
          {drafts.map((api) => (
            <RoutingBar key={api.key} api={api} />
          ))}
          {group.wired.length === 0 && !lead ? (
            <CardNotice testId={`integration-none-${category}`}>{t('providers.integrations.nothingWired')}</CardNotice>
          ) : null}
        </>
      }
      footer={
        filter === 'all' && section.others.length > 0 ? (
          <CardFooterLine testId={`integration-others-${category}`}>
            {t('providers.integrations.othersSupported', {
              vendors: section.others.map((d) => vendorLabel(t, d.category, d.vendor)).join(' · '),
            })}
          </CardFooterLine>
        ) : null
      }
    >
      {facts.length > 0 ? (
        <ProviderTable ordered={drafts.length > 0}>
          {leading.map(renderRow)}
          {lead?.row}
          {following.map(renderRow)}
        </ProviderTable>
      ) : null}
    </ServiceCard>
  );
}
